import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Gauge, Timer, TrendingDown, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface WorkOrderManagementSectionProps {
  data: any;
}

export const WorkOrderManagementSection = ({
  data,
}: WorkOrderManagementSectionProps) => {
  const navigate = useNavigate();
  const workOrderBacklog = data?.workOrderBacklog;
  const assetPerformance = data?.assetPerformanceByType;
  const getMetricColor = (
    value: number,
    goodThreshold: number,
    poorThreshold: number,
    reverse = false
  ) => {
    if (reverse) {
      if (value <= goodThreshold) return "text-status-good";
      if (value <= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    } else {
      if (value >= goodThreshold) return "text-status-good";
      if (value >= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    }
  };
  const handleNavigation = () => {
    navigate("/ax/service-order/landing");
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              Work Order Backlog
            </CardTitle>
            <CustomTooltip message="Current work order backlog showing pending orders, estimated hours, and projected completion timeframe." />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-orange-600">
              {workOrderBacklog?.pendingOrders ?? "NA"}
            </p>
            <p className="text-sm text-muted-foreground">Pending Orders</p>
          </div>
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-xl font-bold">
                {workOrderBacklog?.estimatedHours ?? "NA"}h
              </p>
              <p className="text-sm text-muted-foreground">Est. Hours</p>
            </div>
            <div>
              <p className="text-xl font-bold">
                {workOrderBacklog?.estimatedDuration ?? "NA"} days
              </p>
              <p className="text-sm text-muted-foreground">Est. Duration</p>
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>High Priority:</span>
                <span className="font-medium text-status-poor">
                  {workOrderBacklog?.priorityBreakdown?.highPriority ?? "NA"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Medium Priority:</span>
                <span className="font-medium text-status-fair">
                  {workOrderBacklog?.priorityBreakdown?.mediumPriority ?? "NA"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Low Priority:</span>
                <span className="font-medium text-status-good">
                  {workOrderBacklog?.priorityBreakdown?.lowPriority ?? "NA"}
                </span>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleNavigation}
          >
            View
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              Asset Performance by Type
            </CardTitle>
            <CustomTooltip message="Performance index by asset type showing the top 5 least performing asset categories. Lower scores indicate higher maintenance needs and operational issues." />
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="text-center">
            <p
              className={`text-3xl font-bold ${getMetricColor(
                assetPerformance?.overallPerformanceIndex,
                80,
                70
              )}`}
            >
              {assetPerformance?.overallPerformanceIndex ?? "NA"}
            </p>
            <p className="text-sm text-muted-foreground">
              Overall Performance Index
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className={`h-2 rounded-full ${
                  assetPerformance?.overallPerformanceIndex >= 80
                    ? "bg-green-500"
                    : assetPerformance?.overallPerformanceIndex >= 70
                    ? "bg-yellow-500"
                    : "bg-red-500"
                }`}
                style={{
                  width: `${assetPerformance?.overallPerformanceIndex}%`,
                }}
              />
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-sm font-medium mb-3">
              Top 5 Least Performing Types:
            </p>
            <div className="space-y-2">
              {assetPerformance?.topLeastPerforming &&
              assetPerformance.topLeastPerforming.length > 0 ? (
                assetPerformance.topLeastPerforming.map((asset, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        #{index + 1}
                      </span>
                      <span>{asset.type}</span>
                      {asset.trend === "declining" && (
                        <TrendingDown className="h-3 w-3 text-status-poor" />
                      )}
                      {asset.trend === "improving" && (
                        <TrendingUp className="h-3 w-3 text-status-good" />
                      )}
                      {asset.trend === "stable" && (
                        <div className="h-3 w-3 rounded-full bg-status-fair" />
                      )}
                    </div>
                    <span
                      className={`font-medium ${getMetricColor(
                        asset.score,
                        80,
                        70
                      )}`}
                    >
                      {asset.score}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground flex justify-center items-center py-4">
                  No data available
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
